import React, { useState, useEffect } from 'react';
import { COLORS } from '../config/constants';
import { productionService, projectService, mailService } from '../services/api.service';
import api from '../config/api';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import SendMailModal from '../components/SendMailModal';

const ProductionList = ({ user }) => {
  const [productions, setProductions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingProduction, setEditingProduction] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [showMailModal, setShowMailModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    project: '',
    part_name: '',
    material_type: '',
    operator: '',
    date_from: '',
    date_to: ''
  });
  const fileInputRef = React.useRef(null);

  useEffect(() => {
    loadProductions();
  }, []);

  const loadProductions = async () => {
    try {
      setLoading(true);
      const data = await productionService.getAll();
      setProductions(data);
    } catch (error) {
      console.error('Üretim kayıtları yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (production) => {
    setEditingProduction({
      ...production,
      part_name: production.part_name || '',
      quantity: production.quantity || '',
      weight: production.weight || '',
      operator: production.operator || '',
      notes: production.notes || ''
    });
    setShowEditModal(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/production/${editingProduction.id}`, editingProduction);
      setMessage({ type: 'success', text: 'Üretim kaydı güncellendi!' });
      setShowEditModal(false);
      loadProductions();
    } catch (error) {
      setMessage({ type: 'error', text: 'Güncelleme başarısız!' });
    }
  };

  const handleDelete = (production) => {
    setItemToDelete(production);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;
    
    try {
      await api.delete(`/production/${itemToDelete.id}`);
      setMessage({ type: 'success', text: 'Üretim kaydı silindi!' });
      loadProductions();
    } catch (error) {
      setMessage({ type: 'error', text: 'Silme işlemi başarısız!' });
    } finally {
      setDeleteModalOpen(false);
      setItemToDelete(null);
    }
  };

  const handleExportExcel = async () => {
    try {
      setExporting(true);
      setMessage({ type: 'success', text: 'Excel dosyası indiriliyor...' });
      await productionService.exportExcel(filters);
      setMessage({ type: 'success', text: 'Excel dosyası başarıyla indirildi!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Excel export başarısız!' });
    } finally {
      setExporting(false);
    }
  };

  const handleExportPDF = async () => {
    try {
      setExporting(true);
      setMessage({ type: 'success', text: 'PDF dosyası indiriliyor...' });
      await productionService.exportPDF(filters);
      setMessage({ type: 'success', text: 'PDF dosyası başarıyla indirildi!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'PDF export başarısız!' });
    } finally {
      setExporting(false);
    }
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check file type
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      setMessage({ type: 'error', text: 'Lütfen Excel dosyası (.xlsx veya .xls) seçin!' });
      return;
    }

    try {
      setImporting(true);
      setMessage({ type: 'success', text: 'Excel dosyası yükleniyor...' });
      
      const result = await productionService.importExcel(file);
      
      if (result.success) {
        setMessage({ 
          type: 'success', 
          text: result.message || `Başarılı! ${result.imported} kayıt eklendi.`
        });
      } else {
        setMessage({ type: 'error', text: result.message || 'Import başarısız!' });
      }
      
      // Reload data
      loadProductions();
    } catch (error) {
      console.error('Import error:', error);
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Excel import başarısız!' });
    } finally {
      setImporting(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const canEdit = user?.role === 'admin' || user?.role === 'operator';

  const clearFilters = () => {
    setFilters({
      project: '',
      part_name: '',
      material_type: '',
      operator: '',
      date_from: '',
      date_to: ''
    });
  };

  const handleSendMail = async (recipients, attachExcel, attachPdf) => {
    try {
      await mailService.sendProductionMail(recipients, attachExcel, attachPdf, filters);
      setMessage({ type: 'success', text: `Mail başarıyla ${recipients.length} alıcıya gönderildi!` });
    } catch (error) {
      console.error('Mail gönderme hatası:', error);
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Mail gönderilemedi!' });
    }
  };

  const filteredProductions = productions.filter(production => {
    const matchesSearch = 
      production.part_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      production.project_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      production.material_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      production.operator?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      production.rulo_numarasi?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      production.kanban_numarasi?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesProject = !filters.project || production.project_name?.toLowerCase().includes(filters.project.toLowerCase());
    const matchesPartName = !filters.part_name || production.part_name?.toLowerCase().includes(filters.part_name.toLowerCase());
    const matchesMaterialType = !filters.material_type || production.material_type?.toLowerCase().includes(filters.material_type.toLowerCase());
    const matchesOperator = !filters.operator || production.operator?.toLowerCase().includes(filters.operator.toLowerCase());
    
    let matchesDateFrom = true;
    let matchesDateTo = true;
    
    if (filters.date_from && production.production_date) {
      matchesDateFrom = new Date(production.production_date) >= new Date(filters.date_from);
    }
    
    if (filters.date_to && production.production_date) {
      matchesDateTo = new Date(production.production_date) <= new Date(filters.date_to);
    }
    
    return matchesSearch && matchesProject && matchesPartName && matchesMaterialType && matchesOperator && matchesDateFrom && matchesDateTo;
  });

  // Get unique values from FILTERED productions (only visible records)
  const uniqueProjects = [...new Set(filteredProductions.map(p => p.project_name).filter(Boolean))];
  const uniquePartNames = [...new Set(filteredProductions.map(p => p.part_name).filter(Boolean))];
  const uniqueMaterialTypes = [...new Set(filteredProductions.map(p => p.material_type).filter(Boolean))];
  const uniqueOperators = [...new Set(filteredProductions.map(p => p.operator).filter(Boolean))];

  return (
    <div style={{ 
      padding: '2rem',
      background: 'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%)',
      minHeight: '100vh'
    }}>
      {/* Modern Card Container */}
      <div style={{
        background: '#FFFFFF',
        borderRadius: '16px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
        overflow: 'hidden',
        border: '1px solid rgba(228, 10, 4, 0.1)'
      }}>
        {/* Modern Header with Gradient */}
        <div style={{
          background: 'linear-gradient(135deg, #E40A04 0%, #B00803 100%)',
          padding: '2rem',
          color: '#FFFFFF'
        }}>
          <h1 style={{ fontSize: '2rem', fontWeight: '700', margin: 0, letterSpacing: '-0.5px' }}>
            🏭 Üretim Kayıtları
          </h1>
          <p style={{ fontSize: '1rem', opacity: 0.9, marginTop: '0.5rem' }}>
            Tüm üretim işlemlerini yönetin
          </p>
          
          {/* Stats Bar */}
          <div style={{ display: 'flex', gap: '1.5rem', marginTop: '1.5rem', flexWrap: 'wrap' }}>
            <div style={{
              background: 'rgba(255, 255, 255, 0.15)',
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              backdropFilter: 'blur(10px)'
            }}>
              <div style={{ fontSize: '0.85rem', opacity: 0.8, marginBottom: '0.25rem' }}>Toplam Üretim Ağırlık</div>
              <div style={{ fontSize: '1.5rem', fontWeight: '700' }}>
                {productions.reduce((sum, p) => sum + (parseFloat(p.weight) || 0), 0).toFixed(0)} kg
              </div>
            </div>
            <div style={{
              background: 'rgba(255, 255, 255, 0.15)',
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              backdropFilter: 'blur(10px)'
            }}>
              <div style={{ fontSize: '0.85rem', opacity: 0.8, marginBottom: '0.25rem' }}>Filtreli Kayıt</div>
              <div style={{ fontSize: '1.5rem', fontWeight: '700' }}>{filteredProductions.length}</div>
            </div>
            <div style={{
              background: 'rgba(255, 255, 255, 0.15)',
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              backdropFilter: 'blur(10px)'
            }}>
              <div style={{ fontSize: '0.85rem', opacity: 0.8, marginBottom: '0.25rem' }}>Filtreli Ağırlık</div>
              <div style={{ fontSize: '1.5rem', fontWeight: '700' }}>
                {filteredProductions.reduce((sum, p) => sum + (parseFloat(p.weight) || 0), 0).toFixed(0)} kg
              </div>
            </div>
          </div>
        </div>

        {/* Controls Section - Modern */}
        <div style={{
          padding: '1.5rem 2rem',
          background: '#FFFFFF',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '1rem',
          borderBottom: '1px solid #E8E8E8'
        }}>
          <div style={{ flex: '1', maxWidth: '500px', position: 'relative' }}>
            <span style={{
              position: 'absolute',
              left: '1rem',
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: '1.2rem',
              color: '#999'
            }}>🔍</span>
            <input
              type="text"
              placeholder="Proje, parça, malzeme, operatör, rulo veya kanban ile ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '0.875rem 1rem 0.875rem 3rem',
                border: '2px solid #E8E8E8',
                borderRadius: '10px',
                fontSize: '1rem',
                outline: 'none',
                transition: 'all 0.3s ease'
              }}
              onFocus={(e) => {
                e.target.style.border = '2px solid #E40A04';
                e.target.style.boxShadow = '0 4px 12px rgba(228, 10, 4, 0.15)';
              }}
              onBlur={(e) => {
                e.target.style.border = '2px solid #E8E8E8';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>
          
          {/* Export Buttons */}
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            <button
              onClick={() => setShowFilters(!showFilters)}
              style={{
                padding: '0.875rem 1.75rem',
                background: showFilters ? 'linear-gradient(135deg, #E40A04 0%, #B00803 100%)' : '#FFFFFF',
                color: showFilters ? '#FFFFFF' : '#333333',
                border: showFilters ? 'none' : '2px solid #E40A04',
                borderRadius: '10px',
                fontSize: '0.95rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: showFilters 
                  ? '0 4px 12px rgba(228, 10, 4, 0.3)'
                  : '0 2px 8px rgba(0, 0, 0, 0.08)',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              🔍 Filtreler
            </button>
            <button
              onClick={handleImportClick}
              disabled={importing}
              style={{
                padding: '0.875rem 1.75rem',
                background: '#3B82F6',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '10px',
                fontSize: '0.95rem',
                fontWeight: '600',
                cursor: importing ? 'not-allowed' : 'pointer',
                opacity: importing ? 0.5 : 1,
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              📥 Excel Yükle
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
            />
            <button
              onClick={handleExportExcel}
              disabled={exporting || filteredProductions.length === 0}
              style={{
                padding: '0.875rem 1.75rem',
                background: '#10B981',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '10px',
                fontSize: '0.95rem',
                fontWeight: '600',
                cursor: exporting || filteredProductions.length === 0 ? 'not-allowed' : 'pointer',
                opacity: exporting || filteredProductions.length === 0 ? 0.5 : 1,
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              📊 Excel İndir
            </button>
            <button
              onClick={handleExportPDF}
              disabled={exporting || filteredProductions.length === 0}
              style={{
                padding: '0.875rem 1.75rem',
                background: 'linear-gradient(135deg, #E40A04 0%, #B00803 100%)',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '10px',
                fontSize: '0.95rem',
                fontWeight: '600',
                cursor: exporting || filteredProductions.length === 0 ? 'not-allowed' : 'pointer',
                opacity: exporting || filteredProductions.length === 0 ? 0.5 : 1,
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 12px rgba(228, 10, 4, 0.3)',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              📄 PDF İndir
            </button>
            <button
              onClick={() => setShowMailModal(true)}
              style={{
                padding: '0.875rem 1.75rem',
                background: '#3B82F6',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '10px',
                fontSize: '0.95rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              📧 Mail Gönder
            </button>
          </div>
        </div>

        {/* Message */}
        {message.text && (
          <div style={{
            padding: '1rem',
            margin: '1.5rem 2rem',
            borderRadius: '8px',
            background: message.type === 'success' ? '#d1fae5' : '#fee2e2',
            color: message.type === 'success' ? '#065f46' : '#991b1b'
          }}>
            {message.text}
          </div>
        )}

        {/* Filters Panel */}
        {showFilters && (
          <div style={{ 
            background: COLORS.white, 
            padding: '1.5rem 2rem',
            borderTop: '1px solid #E8E8E8'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '1rem', fontSize: '1.1rem', color: COLORS.text }}>
              Filtreleme Seçenekleri
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: '500' }}>
                  Proje Adı
                </label>
                <input
                  type="text"
                  list="project-names-list-prod"
                  value={filters.project}
                  onChange={(e) => setFilters({...filters, project: e.target.value})}
                  placeholder="Liste seç veya yaz..."
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: `2px solid ${COLORS.background}`,
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    outline: 'none'
                  }}
                />
                <datalist id="project-names-list-prod">
                  {uniqueProjects.map(proj => (
                    <option key={proj} value={proj} />
                  ))}
                </datalist>
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: '500' }}>
                  Parça Adı
                </label>
                <input
                  type="text"
                  list="part-names-list-prod"
                  value={filters.part_name}
                  onChange={(e) => setFilters({...filters, part_name: e.target.value})}
                  placeholder="Liste seç veya yaz..."
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: `2px solid ${COLORS.background}`,
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    outline: 'none'
                  }}
                />
                <datalist id="part-names-list-prod">
                  {uniquePartNames.map(part => (
                    <option key={part} value={part} />
                  ))}
                </datalist>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: '500' }}>
                  Malzeme Türü
                </label>
                <input
                  type="text"
                  list="material-types-list-prod"
                  value={filters.material_type}
                  onChange={(e) => setFilters({...filters, material_type: e.target.value})}
                  placeholder="Liste seç veya yaz..."
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: `2px solid ${COLORS.background}`,
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    outline: 'none'
                  }}
                />
                <datalist id="material-types-list-prod">
                  {uniqueMaterialTypes.map(type => (
                    <option key={type} value={type} />
                  ))}
                </datalist>
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: '500' }}>
                  Operatör
                </label>
                <input
                  type="text"
                  list="operators-list-prod"
                  value={filters.operator}
                  onChange={(e) => setFilters({...filters, operator: e.target.value})}
                  placeholder="Liste seç veya yaz..."
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: `2px solid ${COLORS.background}`,
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    outline: 'none'
                  }}
                />
                <datalist id="operators-list-prod">
                  {uniqueOperators.map(op => (
                    <option key={op} value={op} />
                  ))}
                </datalist>
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: '500' }}>
                  Başlangıç Tarihi
                </label>
                <input
                  type="date"
                  value={filters.date_from}
                  onChange={(e) => setFilters({...filters, date_from: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: `2px solid ${COLORS.background}`,
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    outline: 'none'
                  }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: '500' }}>
                  Bitiş Tarihi
                </label>
                <input
                  type="date"
                  value={filters.date_to}
                  onChange={(e) => setFilters({...filters, date_to: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: `2px solid ${COLORS.background}`,
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    outline: 'none'
                  }}
                />
              </div>
            </div>
            
            <div style={{ marginTop: '1rem' }}>
              <button
                onClick={clearFilters}
                style={{
                  padding: '0.5rem 1rem',
                  background: COLORS.background,
                  color: COLORS.text,
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '0.9rem',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Filtreleri Temizle
              </button>
            </div>
          </div>
        )}

        {/* Table */}
        <div style={{ 
          padding: '0 2rem 2rem 2rem'
        }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '3rem' }}>
              <p style={{ color: COLORS.textLight }}>Yükleniyor...</p>
            </div>
          ) : filteredProductions.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem' }}>
              <p style={{ color: COLORS.textLight }}>Henüz üretim kaydı yok</p>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: COLORS.background }}>
                <th style={tableHeaderStyle}>Proje</th>
                <th style={tableHeaderStyle}>Parça Adı</th>
                <th style={tableHeaderStyle}>Malzeme Türü</th>
                <th style={tableHeaderStyle}>Uzunluk</th>
                <th style={tableHeaderStyle}>Tarih</th>
                <th style={tableHeaderStyle}>Parça Adedi</th>
                <th style={tableHeaderStyle}>Rulo No</th>
                <th style={tableHeaderStyle}>Kanban No</th>
                <th style={tableHeaderStyle}>Operatör</th>
                <th style={tableHeaderStyle}>Ağırlık (kg)</th>
                {canEdit && <th style={tableHeaderStyle}>İşlemler</th>}
              </tr>
            </thead>
            <tbody>
              {filteredProductions.map((production, index) => (
                <tr key={production.id || index} style={{ borderBottom: `1px solid ${COLORS.background}` }}>
                  <td style={{...tableCellStyle, fontWeight: '600'}}>{production.project_name || '-'}</td>
                  <td style={tableCellStyle}>{production.part_name}</td>
                  <td style={tableCellStyle}>{production.material_type || '-'}</td>
                  <td style={{...tableCellStyle, textAlign: 'center'}}>{production.length || '-'}</td>
                  <td style={tableCellStyle}>
                    {production.production_date ? new Date(production.production_date).toLocaleDateString('tr-TR') : '-'}
                  </td>
                  <td style={{...tableCellStyle, textAlign: 'center', fontWeight: '600', color: COLORS.primary}}>{production.quantity}</td>
                  <td style={tableCellStyle}>{production.rulo_numarasi || '-'}</td>
                  <td style={tableCellStyle}>{production.kanban_numarasi || '-'}</td>
                  <td style={tableCellStyle}>{production.operator || '-'}</td>
                  <td style={{...tableCellStyle, textAlign: 'center', fontWeight: '700', color: COLORS.primary}}>
                    {production.weight ? `${parseFloat(production.weight).toFixed(2)} kg` : '-'}
                  </td>
                  {canEdit && (
                    <td style={tableCellStyle}>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          onClick={() => handleEdit(production)}
                          style={{
                            padding: '0.5rem 1rem',
                            background: '#3b82f6',
                            color: COLORS.white,
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '0.85rem',
                            cursor: 'pointer'
                          }}
                        >
                          Düzenle
                        </button>
                        {user?.role === 'admin' && (
                          <button
                            onClick={() => handleDelete(production)}
                            style={{
                              padding: '0.5rem 1rem',
                              background: COLORS.primary,
                              color: COLORS.white,
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '0.85rem',
                              cursor: 'pointer'
                            }}
                          >
                            Sil
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
        </div>

      </div> {/* Close Modern Card */}

      {/* Edit Modal */}
      {showEditModal && editingProduction && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: COLORS.white,
            borderRadius: '12px',
            padding: '2rem',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h2 style={{ marginBottom: '1.5rem' }}>Üretim Kaydını Düzenle</h2>
            <form onSubmit={handleUpdate}>
              <div style={{ display: 'grid', gap: '1rem' }}>
                <FormField label="Parça Adı" value={editingProduction.part_name} 
                  onChange={(v) => setEditingProduction({...editingProduction, part_name: v})} required />
                <FormField label="Miktar" type="number" value={editingProduction.quantity}
                  onChange={(v) => setEditingProduction({...editingProduction, quantity: v})} required />
                <FormField label="Ağırlık (kg)" type="number" step="0.01" value={editingProduction.weight}
                  onChange={(v) => setEditingProduction({...editingProduction, weight: v})} />
                <FormField label="Operatör" value={editingProduction.operator}
                  onChange={(v) => setEditingProduction({...editingProduction, operator: v})} />
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Notlar</label>
                  <textarea
                    value={editingProduction.notes}
                    onChange={(e) => setEditingProduction({...editingProduction, notes: e.target.value})}
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: `1px solid ${COLORS.background}`,
                      borderRadius: '8px',
                      fontSize: '1rem',
                      resize: 'vertical'
                    }}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
                <button type="submit" style={{
                  padding: '0.75rem 1.5rem',
                  background: COLORS.primary,
                  color: COLORS.white,
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}>
                  Kaydet
                </button>
                <button type="button" onClick={() => setShowEditModal(false)} style={{
                  padding: '0.75rem 1.5rem',
                  background: COLORS.background,
                  color: COLORS.text,
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}>
                  İptal
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <DeleteConfirmModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setItemToDelete(null);
        }}
        onConfirm={confirmDelete}
        itemType="üretim kaydı"
      />

      {/* Send Mail Modal */}
      <SendMailModal
        isOpen={showMailModal}
        onClose={() => setShowMailModal(false)}
        onSend={handleSendMail}
        moduleName="Üretim"
      />
    </div>
  );
};

const FormField = ({ label, value, onChange, type = 'text', step, required = false }) => (
  <div>
    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
      {label} {required && <span style={{ color: COLORS.primary }}>*</span>}
    </label>
    <input
      type={type}
      step={step}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      style={{
        width: '100%',
        padding: '0.75rem',
        border: `1px solid ${COLORS.background}`,
        borderRadius: '8px',
        fontSize: '1rem'
      }}
    />
  </div>
);

const tableHeaderStyle = {
  padding: '1rem',
  textAlign: 'left',
  fontSize: '0.875rem',
  fontWeight: '600',
  textTransform: 'uppercase'
};

const tableCellStyle = {
  padding: '1rem',
  fontSize: '0.95rem'
};

export default ProductionList;
